import { ReactNode } from 'react';

import { Avatar, ChakraFlex, ChakraText } from '@equip.health/ui';

import { getPreferredFullName } from '~/lib/util/patient.util';
import { ProviderAlert } from '~/lib/types/providerDashboard';

import AlertPatientPopover from './AlertPatientPopover';
import {
  getAlertHeadline,
  getAlertMetaLabel,
  isUnreadAlert,
} from './alerts.util';

interface AlertRowProps {
  alert: ProviderAlert;
  actions?: ReactNode;
}

const AlertRow = ({ alert, actions }: AlertRowProps) => {
  const { alertId, patientFirstName, patientLastName, patientChosenName } =
    alert;

  const fullName =
    getPreferredFullName(
      patientChosenName ?? '',
      patientFirstName ?? '',
      patientLastName ?? '',
      false,
    ) || 'Unknown patient';
  const isUnread = isUnreadAlert(alert);
  const headline = getAlertHeadline(alert, fullName);
  const metaLabel = getAlertMetaLabel(alert);

  return (
    <ChakraFlex
      alignItems="flex-start"
      borderTop="1px solid"
      borderColor="neutral.line"
      data-testid={`alert-row-${alertId}`}
      data-unread={isUnread}
      gap="14px"
      padding="16px 0"
    >
      <AlertPatientPopover
        alert={alert}
        fullName={fullName}
        triggerContent={<Avatar name={fullName} size="sm" userType="patient" />}
      />

      <ChakraFlex direction="column" flex="1" gap="4px" minWidth="0">
        <ChakraText
          data-testid={`alert-title-${alertId}`}
          fontSize="15px"
          fontWeight={isUnread ? '500' : '400'}
          color="neutral.primary"
          lineHeight="1.4"
        >
          {headline}
        </ChakraText>
        {metaLabel && (
          <ChakraText
            data-testid={`alert-meta-${alertId}`}
            fontSize="13px"
            color="neutral.secondary"
            lineHeight="1.3"
          >
            {metaLabel}
          </ChakraText>
        )}
      </ChakraFlex>

      {actions && (
        <ChakraFlex
          alignItems="center"
          gap="6px"
          flexShrink={0}
          minHeight="32px"
        >
          {actions}
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

export default AlertRow;
